import { Table, TableColumn } from '@vadiun/react-datatable';
import { LiquidacionModel } from '../models/Liquidacion';
import ActionsTable from './ActionsTable';
import { formatNumberToPrice } from 'shared/helpers';
import { capitalizeFirstLetter } from 'app/vehiculos/helpers/utils';

interface Props {
  data: LiquidacionModel[];
  setSelectedRows: (x: LiquidacionModel[]) => void;
  selectedLiquidaciones: LiquidacionModel[];
  onDeleteReservations: (id: string) => void;
  onDeleteLiquidacion: (id: string) => void;
  handlePageChange: (currentPage: number) => void;
}

const LiquidacionTable = (props: Props) => {
  const data = props.data.map((res) => ({
    ...res,
    clientName: res.client.name,
    datetime: res.datetime.format('DD/MM/YYYY'),
    remises_breakdown_remises: res?.remises_breakdown?.remises ?? 0,
    remises_breakdown_envios: res?.remises_breakdown?.envios ?? 0,
    remises_breakdown_lltpais: res?.remises_breakdown?.lltpais ?? 0,
    remises_breakdown_total: res?.remises_breakdown?.total ?? 0,
    buses_breakdown_propios: res?.buses_breakdown?.propios ?? 0,
    buses_breakdown_terciarizados: res?.buses_breakdown?.terciarizados ?? 0,
    buses_breakdown_furgones: res?.buses_breakdown?.furgones ?? 0,
    buses_breakdown_envios: res?.buses_breakdown?.envios ?? 0,
    buses_breakdown_total: res?.buses_breakdown?.total ?? 0,
    cuit: res?.client.cuit ?? null,
  }));

  const columns: TableColumn<typeof data[0]>[] = [
    {
      name: 'id',
      label: 'ID'
    },
    {
      name: 'clientName',
      label: 'Cliente'
    },
    {
      name: 'datetime',
      label: 'Fecha'
    },
    {
      name: 'remises_breakdown_lltpais',
      label: 'Remises/LLT-pais',
      options: { display: false, download: true }
    },
    {
      name: 'remises_breakdown_remises',
      label: 'Remises/Remises',
      options: { display: false, download: true }
    },
    {
      name: 'remises_breakdown_envios',
      label: 'Remises/Envios',
      options: { display: false, download: true }
    },
    {
      name: 'remises_breakdown_total',
      label: 'Remises/Total',
      options: { display: false, download: true }
    },
    {
      name: 'remises_breakdown',
      label: 'Remises',
      options: {
        setCellProps: () => ({ width: '250px' }),
        filter: false,
        customBodyRenderLite: (index) => {
          const current = props.data[index];
          const breakdown = current?.remises_breakdown;
          return (
            <div className="p-3 bg-gray-100 rounded-xl space-y-1 text-gray-500">
              {breakdown && Object.keys(breakdown).length ? (
                Object.entries(breakdown).map(([key, value]) => {
                  return (
                    <div
                      className={
                        'flex justify-between' +
                        (key === 'total'
                          ? ' border-t-2 border-dashed pt-1'
                          : '')
                      }
                    >
                      <p>{capitalizeFirstLetter(key)}:</p>
                      <span>${formatNumberToPrice(value)}</span>
                    </div>
                  );
                })
              ) : (
                <div className="flex justify-between">
                  <p>Total:</p>
                  <span>$0</span>
                </div>
              )}
            </div>
          );
        },
        download: false
      }
    },
    {
      name: 'buses_breakdown_propios',
      label: 'SG/Propios',
      options: { display: false, download: true }
    },
    {
      name: 'buses_breakdown_terciarizados',
      label: 'SG/Terciarizados',
      options: { display: false, download: true }
    },
    {
      name: 'buses_breakdown_furgones',
      label: 'SG/Furgones',
      options: { display: false, download: true }
    },
    {
      name: 'buses_breakdown_envios',
      label: 'SG/Envios',
      options: { display: false, download: true }
    },
    {
      name: 'buses_breakdown_total',
      label: 'SG/Total',
      options: { display: false, download: true }
    },
    {
      name: 'buses_breakdown',
      label: 'SG',
      options: {
        filter: false,
        setCellProps: () => ({ width: '250px' }),
        customBodyRenderLite: (index) => {
          const current = props.data[index];
          const breakdown = current?.buses_breakdown;
          return (
            <div className="p-3 bg-gray-100 space-y-1 text-gray-500 rounded-xl">
              {breakdown && Object.keys(breakdown).length ? (
                Object.entries(breakdown).map(([key, value]) => {
                  return (
                    <div
                      className={
                        'flex justify-between' +
                        (key === 'total'
                          ? ' border-t-2 border-dashed pt-1'
                          : '')
                      }
                    >
                      <p>{capitalizeFirstLetter(key)}:</p>
                      <span>${formatNumberToPrice(value)}</span>
                    </div>
                  );
                })
              ) : (
                <div className="flex justify-between">
                  <p>Total:</p>
                  <span>$0</span>
                </div>
              )}
            </div>
          );
        },
        download: false
      }
    },
    {
      name: 'total_transfer_price',
      label: 'Total viaje'
    },

    {
      name: 'total_expenses',
      label: 'Total gastos'
    },
    {
      name: 'total_price',
      label: 'Precio final'
    },
    {
      name: 'cuit',
      label: 'CUIT',
      options: {
       display: false,   
       download: true   
      }
    },
    {
      name: 'observations',
      label: 'Observaciones',
      options: {
       display: false,   
       download: true   
      }
    },
    {
      label: 'Acciones',
      name: 'id',

      options: {
        download: false,
        setCellHeaderProps: () => ({
          style: { textAlign: 'right' }
        }),
        setCellProps: () => ({
          width: '100px'
        }),
        customBodyRenderLite: (index) => {
          const current = props.data[index];
          return (
            <ActionsTable
              client={current.client.name}
              settlementId={String(current.id)}
              onDeleteReservations={props.onDeleteReservations}
              onDeleteLiquidacion={props.onDeleteLiquidacion}
            />
          );
        }
      }
    }
  ];

  return (
    <div className="w-full">
      <Table
        data={data}
        options={{
          elevation: 0,
          search: false,
          filter: false,
          sort: false,
          selectableRows: 'multiple',
          downloadOptions: {
            filename: 'liquidaciones'
          },
          rowsSelected: props.selectedLiquidaciones.map((liq) =>
            data.findIndex((d) => d.id === liq.id)
          ),
          selectToolbarPlacement: 'none',
          onRowSelectionChange: (_, selection) => {
            props.setSelectedRows(
              selection.map(({ dataIndex }) => props.data[dataIndex])
            );
          },
          onChangePage: props.handlePageChange,
        }}
        title={''}
        columns={columns}
      />
    </div>
  );
};
export default LiquidacionTable;
